import { useService } from 'solid-services'
import { createEffect, createResource, createSignal, untrack } from 'solid-js'
import { handleGetPublished } from './resource/handleGetPublished'
import { handleGetSubscribed } from './resource/handleGetSubscribed'
import { handleGetTaken } from './resource/handleGetTaken'
import { handleGetMine } from './resource/handleGetMine'

import type { Setup } from '@here/api'
import { userId } from '@state'
import { useFilters } from './filter'

export enum ListTypes {
	published = 'published',
	mine = 'mine',
	subscribed = 'subscribed',
	taken = 'taken',
}

export type Pagination = { limit: number; offset: number }
const resourceOptions = { initialValue: [] as Setup[] }
export type ListLayout = 'noramlised' | 'image'

export function useList() {
	const [hasMore, setHasMore] = createSignal(true)
	const { fetchFilters } = useService(useFilters)()
	const [activeLayout, setActveLayout] = createSignal<ListLayout>('image')
	const [listType, setListType] = createSignal(ListTypes.published)
	const [list, setList] = createSignal<Setup[]>([])
	const [pagination, setPagination] = createSignal<Pagination>({ limit: 5, offset: 0 })

	const whenOfTypeGetFilters = (x: ListTypes) => () =>
		listType() === x ? { filters: fetchFilters(), pagination: pagination() } : undefined

	const [published, { refetch: refetchPublished }] = createResource(
		whenOfTypeGetFilters(ListTypes.published),
		handleGetPublished,
		resourceOptions,
	)

	const [subscribed, { refetch: refetchSubscribed }] = createResource(
		() => ({
			listType: listType(),
			filters: fetchFilters(),
			id: userId(),
			pagination: pagination(),
		}),
		({ listType, filters, id, pagination }) =>
			handleGetSubscribed(listType, filters, id, pagination),
		resourceOptions,
	)

	const [mine, { refetch: refetchMine }] = createResource(
		() => ({
			listType: listType(),
			id: userId(),
			pagination: pagination(),
		}),
		({ listType, id, pagination }) => handleGetMine(listType, id, pagination),
		resourceOptions,
	)

	const [taken, { refetch: refetchTaken }] = createResource(
		() => ({
			listType: listType(),
			filters: fetchFilters(),
			id: userId(),
			pagination: pagination(),
		}),
		({ listType, filters, id, pagination }) =>
			handleGetTaken(listType, filters, id, pagination),
		resourceOptions,
	)

	// fan-in the results per type (unchanged)
	createEffect(() => {
		const l = untrack(listType)
		const incoming = subscribed()
		if (l !== ListTypes.subscribed || !incoming) return
		if (incoming.length === 0) setHasMore(false)
		else {
			setList((prev) => [...prev, ...incoming])
			setHasMore(true)
		}
	})

	createEffect(() => {
		const l = untrack(listType)
		const incoming = published()
		if (l !== ListTypes.published || !incoming) return
		if (incoming.length === 0) setHasMore(false)
		else {
			setList((prev) => [...prev, ...incoming])
			setHasMore(true)
		}
	})

	createEffect(() => {
		const l = untrack(listType)
		const incoming = taken()
		if (l !== ListTypes.taken || !incoming) return
		if (incoming.length === 0) setHasMore(false)
		else {
			setList((prev) => [...prev, ...incoming] as any)
			setHasMore(true)
		}
	})

	createEffect(() => {
		const l = untrack(listType)
		const incoming = mine()
		if (l !== ListTypes.mine || !incoming) return
		if (incoming.length === 0) setHasMore(false)
		else {
			setList((prev) => [...prev, ...incoming])
			setHasMore(true)
		}
	})

	createEffect(() => {
		listType()
		fetchFilters()
		setList([])
		setPagination({ limit: 5, offset: 0 })
		setHasMore(true)
	})

	const refetchCurrent = () => {
		// reset to defaults
		setList([])
		setPagination({ limit: 5, offset: 0 })
		setHasMore(true)

		// refetch the active source
		switch (listType()) {
			case ListTypes.published:
				return refetchPublished()
			case ListTypes.subscribed:
				return refetchSubscribed()
			case ListTypes.taken:
				return refetchTaken()
			case ListTypes.mine:
				return refetchMine()
		}
	}

	const isLoading = () =>
		published.loading || subscribed.loading || taken.loading || mine.loading

	return {
		activeLayout,
		hasMore,
		isLoading,
		list,
		listType,
		pagination,
		setActveLayout,
		setListType,
		setPagination,
		refetchCurrent,
		mine,
	}
}
